/**
 * Hybrid Storage Implementation for AWS Amplify Auth
 * 
 * Uses cookies as primary storage (reliable on Safari iOS) with localStorage fallback.
 * This solves Safari iOS logout-on-refresh issues while maintaining existing sessions.
 * 
 * Storage Strategy:
 * - Reads: Try cookies first, fall back to localStorage
 * - Writes: Write to both cookies and localStorage
 * - Migration: Automatically migrates localStorage sessions to cookies
 */

class HybridStorage {
	constructor() {
		// Cookie settings
		this.cookieExpireDays = 30;
		this.cookiePath = '/';
		// Use 'lax' instead of 'strict' for Safari iOS compatibility
		// 'lax' allows cookies on top-level navigation (like refreshes) while maintaining security
		this.cookieSameSite = 'lax';
	}

	/**
	 * Set a cookie
	 */
	setCookie(name, value, days) {
		const date = new Date();
		date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
		const expires = "expires=" + date.toUTCString();
		
		// Determine domain - handle localhost and production
		const hostname = window.location.hostname;
		const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
		
		// For production, use root domain (e.g., .zapatest.com)
		let domain = '';
		if (!isLocalhost && hostname.includes('.')) {
			const parts = hostname.split('.');
			// Use last two parts (e.g., example.com from www.example.com)
			domain = `;domain=.${parts.slice(-2).join('.')}`;
		}
		
		const secure = window.location.protocol === 'https:' ? ';secure' : '';
		document.cookie = `${name}=${value};${expires};path=${this.cookiePath}${domain}${secure};samesite=${this.cookieSameSite}`;
	}

	/**
	 * Get a cookie
	 */
	getCookie(name) {
		const nameEQ = name + "=";
		const ca = document.cookie.split(';');
		for (let i = 0; i < ca.length; i++) {
			let c = ca[i];
			while (c.charAt(0) === ' ') {
				c = c.substring(1, c.length);
			}
			if (c.indexOf(nameEQ) === 0) {
				return c.substring(nameEQ.length, c.length);
			}
		}
		return null;
	}

	/**
	 * Remove a cookie
	 */
	removeCookie(name) {
		const hostname = window.location.hostname;
		const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
		
		let domain = '';
		if (!isLocalhost && hostname.includes('.')) {
			const parts = hostname.split('.');
			domain = `;domain=.${parts.slice(-2).join('.')}`;
		}
		
		document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=${this.cookiePath}${domain}`;
	}

	/**
	 * AWS Amplify Auth Storage Interface: setItem
	 * Synchronously sets an item in storage
	 */
	setItem(key, value) {
		// Write to both cookie and localStorage for transition/redundancy
		try {
			this.setCookie(key, value, this.cookieExpireDays);
			// Safari silently drops cookies over ~4KB. Verify the write took;
			// otherwise delete any stale cookie so getItem falls through to the
			// fresh localStorage value instead of returning an old token.
			const readBack = this.getCookie(key);
			if (readBack !== value) {
				console.warn('HybridStorage: Cookie write verification failed for key, removing stale cookie:', key);
				this.removeCookie(key);
			}
		} catch (e) {
			console.warn('HybridStorage: Failed to set cookie:', e);
		}
		
		try {
			localStorage.setItem(key, value);
		} catch (e) {
			console.warn('HybridStorage: Failed to set localStorage:', e);
		}
		
		return value;
	}

	/**
	 * AWS Amplify Auth Storage Interface: getItem
	 * Synchronously retrieves an item from storage
	 */
	getItem(key) {
		// Try cookie first (more reliable on Safari iOS)
		let value = this.getCookie(key);
		
		// Fall back to localStorage if cookie not found
		if (!value) {
			try {
				value = localStorage.getItem(key);
				// If found in localStorage, migrate to cookie
				if (value) {
					console.log('HybridStorage: Migrating auth token from localStorage to cookie for Safari iOS compatibility');
					this.setCookie(key, value, this.cookieExpireDays);
				}
			} catch (e) {
				console.warn('HybridStorage: Failed to read localStorage:', e);
			}
		}
		
		return value;
	}

	/**
	 * AWS Amplify Auth Storage Interface: removeItem
	 * Synchronously removes an item from storage
	 */
	removeItem(key) {
		// Remove from both storage mechanisms
		try {
			this.removeCookie(key);
		} catch (e) {
			console.warn('HybridStorage: Failed to remove cookie:', e);
		}
		
		try {
			localStorage.removeItem(key);
		} catch (e) {
			console.warn('HybridStorage: Failed to remove localStorage:', e);
		}
		
		return null;
	}

	/**
	 * AWS Amplify Auth Storage Interface: clear
	 * Clears all items from storage
	 */
	clear() {
		// Clear all Cognito-related cookies
		const cookies = document.cookie.split(';');
		for (let i = 0; i < cookies.length; i++) {
			const cookie = cookies[i];
			const eqPos = cookie.indexOf('=');
			const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
			// Only clear Cognito and AWS related cookies
			if (name.includes('CognitoIdentityServiceProvider') || 
			    name.includes('amplify') || 
			    name.includes('aws')) {
				this.removeCookie(name);
			}
		}
		
		// Clear localStorage
		try {
			localStorage.clear();
		} catch (e) {
			console.warn('HybridStorage: Failed to clear localStorage:', e);
		}
		
		return null;
	}
}

// Export a singleton instance
export default new HybridStorage();

